/**
 * Reverb: ConvolverNode with an impulse response synthesized at load from size / decay / damping / preDelay.
 * The IR is exponentially decaying decorrelated stereo noise through a one-pole lowpass whose cutoff falls over
 * time (damping). Regenerated (debounced 300 ms) when the parameters change.
 */
import { makeRng } from '../modulation';
import { clamp, targetTau, type F32 } from '../params';

export interface ReverbParams { size: number; decaySec: number; damping: number; preDelayMs: number }

export const MAX_IR_SEC = 10;

/** Frames of the synthesized IR: preDelay + decay (capped), never shorter than 0.1 s. */
export function impulseLength(sampleRate: number, p: ReverbParams): number {
  const pre = Math.floor((clamp(p.preDelayMs, 0, 200) / 1000) * sampleRate);
  const decay = Math.max(0.1, Math.min(MAX_IR_SEC, p.decaySec));
  return pre + Math.floor(decay * sampleRate);
}

/** Stereo IR as two Float32Arrays, each normalised to unit energy. */
export function synthesizeImpulse(sampleRate: number, p: ReverbParams, seed = 12345): F32[] {
  const n = impulseLength(sampleRate, p);
  const pre = Math.floor((clamp(p.preDelayMs, 0, 200) / 1000) * sampleRate);
  const decay = Math.max(0.1, Math.min(MAX_IR_SEC, p.decaySec));
  const decayFrames = decay * sampleRate;
  const size = clamp(p.size, 0, 1);
  const damping = clamp(p.damping, 0, 1);
  // build-up: bigger rooms take longer to become diffuse
  const buildFrames = Math.max(1, Math.floor((0.005 + 0.09 * size) * sampleRate));
  // damping: lowpass cutoff falls from fHi to fLo over the decay
  const fHi = 18000 * (1 - 0.5 * damping);
  const fLo = 300 + 6000 * (1 - damping);
  const out: F32[] = [];
  for (let c = 0; c < 2; c++) {
    const rng = makeRng(seed + c * 7919);
    const ir = new Float32Array(n);
    let y = 0;
    let energy = 0;
    for (let i = pre; i < n; i++) {
      const t = i - pre;
      const env = Math.exp((-6.908 * t) / decayFrames) * Math.min(1, t / buildFrames);
      const fc = fHi * Math.pow(fLo / fHi, t / decayFrames);
      const a = 1 - Math.exp((-2 * Math.PI * fc) / sampleRate);
      const x = rng() * 2 - 1;
      y += a * (x - y);
      const v = y * env;
      ir[i] = v;
      energy += v * v;
    }
    const norm = energy > 0 ? 1 / Math.sqrt(energy) : 1;
    for (let i = pre; i < n; i++) ir[i]! *= norm;
    out.push(ir);
  }
  return out;
}

export class Reverb {
  readonly input: GainNode;
  readonly output: GainNode;
  /** the convolver's direct input (shimmer feeds back here) */
  readonly convolverIn: GainNode;
  private conv: ConvolverNode;
  private params: ReverbParams;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  constructor(private readonly ctx: BaseAudioContext, params: ReverbParams) {
    this.params = { ...params };
    this.input = ctx.createGain();
    this.convolverIn = ctx.createGain();
    this.output = ctx.createGain();
    this.conv = ctx.createConvolver();
    this.conv.normalize = false;
    this.input.connect(this.convolverIn);
    this.convolverIn.connect(this.conv);
    this.conv.connect(this.output);
    this.regenerate();
  }
  get wetOutput(): AudioNode { return this.output; }
  setParams(p: ReverbParams): void {
    const changed = p.size !== this.params.size || p.decaySec !== this.params.decaySec || p.damping !== this.params.damping || p.preDelayMs !== this.params.preDelayMs;
    this.params = { ...p };
    if (!changed) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.timer = null; if (!this.disposed) this.regenerate(); }, 300);
  }
  private regenerate(): void {
    const sr = this.ctx.sampleRate;
    const ir = synthesizeImpulse(sr, this.params);
    const buf = this.ctx.createBuffer(2, ir[0]!.length, sr);
    buf.copyToChannel(ir[0]!, 0);
    buf.copyToChannel(ir[1]!, 1);
    // swap convolvers with a short crossfade so a live parameter edit does not click
    const next = this.ctx.createConvolver();
    next.normalize = false;
    next.buffer = buf;
    const t = this.ctx.currentTime;
    const old = this.conv;
    const fadeOut = this.ctx.createGain();
    fadeOut.gain.value = 1;
    try { old.disconnect(); } catch { /* not connected */ }
    old.connect(fadeOut);
    fadeOut.connect(this.output);
    targetTau(fadeOut.gain, 0, t, 0.05);
    this.convolverIn.connect(next);
    next.connect(this.output);
    this.conv = next;
    setTimeout(() => { try { old.disconnect(); fadeOut.disconnect(); } catch { /* gone */ } }, 600);
  }
  dispose(): void {
    this.disposed = true;
    if (this.timer) clearTimeout(this.timer);
    try { this.input.disconnect(); this.convolverIn.disconnect(); this.conv.disconnect(); this.output.disconnect(); } catch { /* gone */ }
  }
}
